// src/components/SubjectNode.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Handle, Position } from 'reactflow';
import clsx from 'clsx';
import { STATUS } from '../utils/careerLogic.js';
import { useOptionalStore } from '../store/store';
import { NODE_WIDTH, NODE_HEIGHT, NODE_STYLES } from '../utils/graphUtils.js';
import './SubjectNode.css';

const SubjectNode = ({ data, id }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [isLongPressing, setIsLongPressing] = useState(false);

  // Cleanup del timer cuando el componente se desmonta
  useEffect(() => {
    return () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
      }
    };
  }, [longPressTimer]);

  // Log para debug
  // console.log('🎯 SubjectNode renderizando:', id, 'hidden:', data?.hidden, 'data:', data);


  const store = useOptionalStore();

  // Retornar null SOLO si está hidden (CBC colapsado)
  if (data?.hidden === true) {
    return null;
  }

  if (!data || !data.materia) {
    console.error('SubjectNode: No data or materia provided for node', id);
    return (
      <div className="subject-node subject-node-error" style={{ 
        width: `${NODE_WIDTH}px`,
        height: `${NODE_HEIGHT}px`
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5em', marginBottom: 4 }}>ℹ️</div>
          <div>Error: Datos ID {id}</div>
        </div>
      </div>
    );
  }

  const { 
    materia, 
    onMateriaChangeStatus, 
    esMateriaSeleccionadaParaExplorar,
    esPrerrequisitoDeExplorada,
    esHabilitadaPorExplorada,
    onExplorarMateria,
    recentlyChangedIds,
    selectionActive
  } = data;

  

  const cambiarEstadoMateria = () => {
    if (onMateriaChangeStatus && typeof onMateriaChangeStatus === 'function') {
      let nuevoEstado = materia.estado;
      
      // Ciclo de estados: NO_CURSADA -> PUEDE_CURSAR -> CURSANDO -> CURSADA_APROBADA -> PUEDE_CURSAR
      switch (materia.estado) {
        case STATUS.NO_CURSADA:
          if (materia.puedeCursar) {
            nuevoEstado = STATUS.CURSANDO;
          }
          break;
        case STATUS.PUEDE_CURSAR:
          nuevoEstado = STATUS.CURSANDO;
          break;
        case STATUS.CURSANDO:
          nuevoEstado = STATUS.CURSADA_APROBADA;
          break;
        case STATUS.CURSADA_APROBADA:
          // Volver a PUEDE_CURSAR si no es arrastre (-3)
          if (materia.notaFinal !== -3) nuevoEstado = STATUS.PUEDE_CURSAR;
          break;
        default:
          break;
      }
      
      if (onMateriaChangeStatus) {
        onMateriaChangeStatus(materia.id, nuevoEstado);
      } else if (store?.setSubjectStatus) {
        const status = nuevoEstado === STATUS.CURSADA_APROBADA ? 'aprobada' : (nuevoEstado === STATUS.CURSANDO ? 'cursando' : 'pendiente');
        store.setSubjectStatus(materia.id, status);
      }
    }
  };

  const explorarMateria = () => {
    if (onExplorarMateria && typeof onExplorarMateria === 'function') {
      onExplorarMateria(materia.id);
    }
  };

  const handleClick = (event) => {
    event.stopPropagation();
    
    if (isLongPressing) {
      setIsLongPressing(false);
      return;
    }

    if (esMateriaSeleccionadaParaExplorar) {
      cambiarEstadoMateria();
    } else if (materia.estado === STATUS.CURSADA_APROBADA) {
      explorarMateria();
    } else if (materia.estado === STATUS.PUEDE_CURSAR || materia.estado === STATUS.CURSANDO) {
      cambiarEstadoMateria();
    } else if (materia.estado === STATUS.NO_CURSADA && !materia.puedeCursar) {
      explorarMateria();
    }
  };

  const getStatusIcon = () => {
    if (esMateriaSeleccionadaParaExplorar) {
      return 'ℹ️';
    }
    
    switch (materia.estado) {
      case STATUS.CURSADA_APROBADA:
        return '✅';
      case STATUS.CURSANDO:
        return '▶️';
      case STATUS.PUEDE_CURSAR:
        return '⏳';
      case STATUS.NO_CURSADA:
      default:
        return materia.puedeCursar ? '⏳' : '🔒';
    }
  };

  const getStatusClass = () => {
    if (esMateriaSeleccionadaParaExplorar) return 'status-explorar-seleccionada';
    if (esPrerrequisitoDeExplorada) return 'status-explorar-prerrequisito';
    if (esHabilitadaPorExplorada) return 'status-explorar-habilitada';
    if (selectionActive) return 'status-muted';
    if (!materia.puedeCursar && materia.estado === STATUS.NO_CURSADA) return 'status-no-disponible';
    if (materia.notaFinal === -2 && materia.estado === STATUS.CURSADA_APROBADA) return 'status-arrastre-final';
    if (materia.notaFinal === -3 && materia.estado === STATUS.CURSADA_APROBADA) return 'status-arrastre-cursada'; 
    switch (materia.estado) {
      case STATUS.PUEDE_CURSAR: return 'status-puede-cursar';
      case STATUS.CURSANDO: return 'status-cursando';
      case STATUS.CURSADA_APROBADA: return 'status-cursada-aprobada';
      default: return 'status-no-cursada';
    }
  };

  const getStatusColor = () => {
    switch (materia.estado) {
      case STATUS.CURSADA_APROBADA:
        return 'var(--success-color)';
      case STATUS.CURSANDO:
        return 'var(--status-cursando-border)';
      case STATUS.PUEDE_CURSAR:
        return 'var(--status-puede-cursar-border)';
      case STATUS.NO_CURSADA:
      default:
        return materia.puedeCursar ? 'var(--status-puede-cursar-border)' : 'var(--status-no-disponible-border)';
    }
  };

  const getExplorationIndicatorClass = () => {
    if (esMateriaSeleccionadaParaExplorar) return 'explorar-seleccionada';
    if (esPrerrequisitoDeExplorada) return 'explorar-prerrequisito';
    if (esHabilitadaPorExplorada) return 'explorar-habilitada';
    return '';
  };

  const statusColor = getStatusColor();

  // console.log('✅ SubjectNode va a renderizar:', id, 'statusColor:', statusColor);

  const justUpdated = recentlyChangedIds instanceof Set && recentlyChangedIds.has(String(materia.id));

  return (
    <div>
      <Handle 
        type="target" 
        position={Position.Top} 
        id={`${id}-target`}
        className="subject-node-handle"
        style={{
          background: statusColor
        }}
      />
      
      <Handle 
        type="source" 
        position={Position.Bottom} 
        id={`${id}-source`}
        className="subject-node-handle"
        style={{
          background: statusColor
        }}
      />
      
      <div
        className={clsx('subject-node', getStatusClass(), { 'just-updated': justUpdated })}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleClick(e);
          }
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onTouchStart={(e) => {
          // Preparar detección de long-press y evitar scroll/click fantasma
          const timer = setTimeout(() => {
            setIsLongPressing(true);
          }, 500);
          setLongPressTimer(timer);
        }}
        onTouchEnd={(e) => {
          // Cancelar long-press
          if (longPressTimer) {
            clearTimeout(longPressTimer);
            setLongPressTimer(null);
          }
          // Si no fue long-press, tratarlo como tap (ejecutar la misma lógica que click)
          if (!isLongPressing) {
            e.preventDefault();
            handleClick(e);
          }
          setIsLongPressing(false);
        }}
        role="button"
        tabIndex={0}
        aria-label={`${materia.nombre} - ${materia.estado.replace('_', ' ')}`}
        style={{
          width: `${NODE_WIDTH}px`, 
          height: `${NODE_HEIGHT}px`,
          opacity: NODE_STYLES[materia.categoria]?.opacity || 1
        }}
      >
        {justUpdated && (materia.estado === STATUS.CURSADA_APROBADA) && (
          <div className="animated-check">✅</div>
        )}
        {/* Efecto de brillo sutil en hover */}
        {isHovered && <div className="subject-node-shimmer" />}
        
        {/* Header con icono de estado */}
        <div className="subject-node-header">
          <div className="subject-node-status-icon">
            <span>{getStatusIcon()}</span>
          </div>
          {(esMateriaSeleccionadaParaExplorar || esPrerrequisitoDeExplorada || esHabilitadaPorExplorada) && (
            <div className={clsx('subject-node-exploration-indicator', getExplorationIndicatorClass())} />
          )}
        </div>
        
        {/* Nombre de la materia */}
        <div className="subject-node-content">
          <strong className="subject-node-title">
            {materia.nombre}
          </strong>
        </div>
        
        {/* Footer con información adicional */}
        <div className="subject-node-footer">
          {materia.creditos && (
            <span className="subject-node-credits">
              {materia.creditos}c
            </span>
          )}
          {materia.notaFinal > 0 && (
            <span className="subject-node-grade">
              {materia.notaFinal}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

SubjectNode.propTypes = {
  data: PropTypes.shape({
    materia: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      nombre: PropTypes.string,
      estado: PropTypes.string,
      puedeCursar: PropTypes.bool,
      notaFinal: PropTypes.number,
      creditos: PropTypes.number,
      categoria: PropTypes.string,
    }).isRequired,
    onMateriaChangeStatus: PropTypes.func,
    esMateriaSeleccionadaParaExplorar: PropTypes.bool,
    esPrerrequisitoDeExplorada: PropTypes.bool,
    esHabilitadaPorExplorada: PropTypes.bool,
    onExplorarMateria: PropTypes.func,
  }).isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default SubjectNode;